import { useEffect, useState } from "react";
import { existingMailDb } from "../db/client.ts";

export interface Me {
  sub: string;
  email: string;
  name: string;
  accounts: { id: string; email: string }[];
}

export type SessionState =
  | { status: "loading" }
  | { status: "anon" }
  /** offline: the server couldn't be reached and this is the last known session */
  | { status: "ok"; me: Me; offline: boolean }
  | { status: "error"; message: string };

const ME_KEY = "homing.me";
export const CACHE_KEYS = [ME_KEY, "homing.accounts", "homing.account"];

function cached(): Me | null {
  try {
    const raw = localStorage.getItem(ME_KEY);
    return raw ? (JSON.parse(raw) as Me) : null;
  } catch {
    return null;
  }
}

function clearCache() {
  try {
    for (const k of CACHE_KEYS) localStorage.removeItem(k);
  } catch {
    /* private mode */
  }
}

export function useSession(): SessionState {
  const [s, set] = useState<SessionState>({ status: "loading" });
  useEffect(() => {
    let live = true;
    const offline = (message: string) => {
      const me = cached();
      set(me ? { status: "ok", me, offline: true } : { status: "error", message });
    };
    fetch("/api/me", { credentials: "same-origin" })
      .then(async (r) => {
        if (!live) return;
        if (r.status === 401) {
          clearCache();
          return set({ status: "anon" });
        }
        if (!r.ok) return offline(`Server answered ${r.status}.`);
        const me = (await r.json()) as Me;
        try {
          localStorage.setItem(ME_KEY, JSON.stringify(me));
        } catch {
          /* private mode */
        }
        set({ status: "ok", me, offline: false });
      })
      .catch(() => live && offline("The server can't be reached."));
    return () => {
      live = false;
    };
  }, []);
  return s;
}

export function login(returnTo = location.pathname) {
  location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
}

export async function logout() {
  // Mail stays on a shared machine otherwise: delete the local databases first.
  await existingMailDb()
    ?.wipe()
    .catch((e: unknown) => console.warn("wipe failed", e));
  clearCache();
  await fetch("/auth/logout", { method: "POST", credentials: "same-origin", headers: { "X-Homing": "1" } });
  location.assign("/");
}
